import { motion } from 'motion/react';
import { useRef } from 'react';
import { useIsInView } from '../../hooks/useIsInView';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { isFinnishPage } from '../../lib/blogPaths';
import { getWpImageSrc } from '../../lib/wpMedia';

const DEFAULT_BLOG_POSTS = [
  {
    title: 'What is software architecture and why is it important?',
    excerpt:
      'Software powers almost every aspect of our everyday life. One fundamental concept is software architecture. But what is it, and why does it matter?',
    image: '/blog-placeholders/blog-1.png',
    href: '/blog/what-is-software-architecture-and-why-is-it-important',
  },
  {
    title: 'Building Healthcare Apps That Scale: Lessons from Mehiläinen',
    excerpt:
      'Deep dive into architecture decisions, security considerations, and UX patterns for healthcare applications serving millions.',
    image: '/blog-placeholders/blog-2.png',
    href: '/blog',
  },
  {
    title: 'The Future of Nordic Software Development',
    excerpt:
      'Trends shaping how we build software: AI tools, cloud-native architecture, and the evolving role of senior developers.',
    image: '/blog-placeholders/blog-3.png',
    href: '/blog',
  },
];

export function BlogPreviewSection({ posts: postsProp }) {
  const ref = useRef(null);
  const router = useRouter();
  const isInView = useIsInView(ref, { once: true, margin: '-100px' });
  const isFinnish = isFinnishPage(router?.pathname);
  const posts = postsProp?.length ? postsProp : DEFAULT_BLOG_POSTS;

  const heading = isFinnish ? 'Ajatuksia tiimiltämme' : 'Insights from Our Team';
  const subtitle = isFinnish ? 'Viimeisimmät ajatuksemme ja artikkelimme' : 'Our latest thoughts & articles';
  const readMore = isFinnish ? 'Lue lisää' : 'Read more';
  const viewAllText = isFinnish ? 'Kaikki blogikirjoitukset' : 'View all insights';
  const viewAllHref = isFinnish ? '/blogi' : '/blog';

  return (
    <section ref={ref} className="py-32 bg-white [content-visibility:auto]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 min-w-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-6">
            {heading}
          </h2>
          <p className="text-xl text-gray-500">
            {subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {posts.map((post, index) => (
            <motion.article
              key={post.slug ?? post.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-[0_12px_40px_-10px_rgba(0,0,0,0.12)] hover:-translate-y-1 transition-all duration-300 group"
            >
              <Link
                href={post.href ?? (post.slug ? `/blog/${post.slug}` : '/blog')}
                className="block cursor-pointer no-underline touch-manipulation"
              >
                <div className="h-48 bg-gray-100 relative overflow-hidden">
                  <Image
                    src={(getWpImageSrc(post.image) || post.image) ?? '/blog-placeholders/blog-1.png'}
                    alt={post.title ?? ''}
                    fill
                    unoptimized
                    sizes="(max-width: 768px) 100vw, 33vw"
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#1a1a1a] mb-3 leading-tight group-hover:text-[#0f172a] transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-[#6b7280] mb-4 leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-end">
                    <span className="text-sm font-medium text-brand">{readMore}</span>
                    <ArrowRight className="w-5 h-5 text-brand ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <Link
            href={viewAllHref}
            className="group inline-flex items-center gap-2 text-brand font-medium text-lg hover:gap-3 transition-all touch-manipulation"
          >
            {viewAllText}
            <ArrowRight className="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:translate-x-1" aria-hidden />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
